import React, { useMemo } from 'react';
import {
  AmeyoCallNotifications,
  AnomalyNotificationMap,
  NotificationTabTemplateMap,
  TemplateInfoMap,
  TemplateTypes,
  WidgetStatuses
} from './constants';
import { ICTAS, NotificationListProps, NotificationsData, Widget } from './interfaces';
import { getTimeDifference, hasNotCompletedAction } from './utils';
import styles from './Notifications.module.scss';
import Typography from '@navi/web-ui/lib/primitives/Typography/index';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { triggerNotificationAction } from './NotificationsActions';
import cx from 'classnames';
import { navigateToCaseDetails } from '../../utils/commonUtils';
import { useNavigate } from 'react-router-dom';
import APP_ROUTES, { APP_ROUTES_PATHS_WITH_PATH_PARAM } from 'src/layout/Routes';
import { ECallingType } from 'src/pages/CaseDetails/feedbackForm/interfaces/index.type';
import { createQueryParams } from 'src/utils/QueryParamsHelper';
import { addClickstreamEvent } from '../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../service/clickStream.constant';
import { interpolatePathParams } from '@cp/src/utils/interpolate';
import { TAB_KEYS as RIGHT_SIDE_TABS } from '@cp/src/pages/CaseDetails/constants/RightSidebar.constant';
import { TAB_KEYS } from '@cp/src/pages/CaseDetails/constants';
import { AnomalyStatus, AnomalyTableKeys } from '@cp/src/pages/AnomalyTracker/types';
import { getReporteesWithAllocation } from '../sidebar/actions/escalationsAction';
import { setShowLogoutModal } from '@cp/src/reducers/commonSlice';

const NotificationList: React.FC<NotificationListProps> = ({
  toggleNotifications,
  notifications,
  activeTab,
  showNotifications
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const templatesMap = useSelector((state: RootState) => state?.notification.templatesMap);

  const phoneNumber = useSelector((state: RootState) => state.common.userData?.phoneNumber);
  const agentReferenceId = useSelector((state: RootState) => state.common.userData?.referenceId);
  const countOfEscalationEnabled = useSelector(
    (state: RootState) => state?.common?.featureFlags?.countOfEscalationEnabled
  );

  const handleNotificationClick = (
    notification: NotificationsData,
    widget: Widget,
    templateName: string
  ) => {
    toggleNotifications(false);
    const { params } = notification;
    if (
      [
        TemplateTypes.CASE_ALLOCATED_NOTIFICATION_TEMPLATE,
        TemplateTypes.CASE_DE_ALLOCATED_NOTIFICATION_TEMPLATE,
        TemplateTypes.COLLECTION_PAUSE_EFFORTS_CASE_DEALLOCATED
      ].includes(templateName)
    ) {
      navigate(APP_ROUTES.CASES.path);
    } else if (AmeyoCallNotifications.includes(templateName)) {
      const searchParams = createQueryParams({
        AMEYO: { phoneNumber: notification?.params?.phoneNumber, method: ECallingType.AMEYO }
      });
      navigateToCaseDetails(
        params?.customerReferenceId,
        params?.loanAccountNumber,
        params?.caseReferenceId,
        params?.businessVertical,
        navigate,
        APP_ROUTES.CASE_DETAIL.path,
        searchParams
      );
    } else if (
      [TemplateTypes.SUPPORT_REQUEST_RECEIVED, TemplateTypes.SUPPORT_REQUEST_RESOLVED].includes(
        templateName
      )
    ) {
      const path = interpolatePathParams(APP_ROUTES.CASE_DETAIL.path, {
        selectedTab: TAB_KEYS.OVERVIEW,
        loanId: params?.loanAccountNumber,
        customerId: params?.customerReferenceId,
        caseReferenceId: params?.caseReferenceId,
        caseBusinessVertical: params?.businessVertical
      });
      navigate(
        { pathname: path },
        { replace: false, state: { rightSideTab: RIGHT_SIDE_TABS.TASKS } }
      );
    } else if (AnomalyNotificationMap[templateName]) {
      const queryParams = createQueryParams({
        [AnomalyTableKeys.ANOMALY_TABLE_ACTION]: {
          selectedId: params?.anomalyReferenceId
        }
      });
      navigate(`${APP_ROUTES_PATHS_WITH_PATH_PARAM.ANOMALY_TRACKER.open}${queryParams}`);
    } else if (templateName === TemplateTypes.COLLECTION_ESCALATION_RAISED) {
      dispatch(getReporteesWithAllocation(agentReferenceId || '', countOfEscalationEnabled));
      dispatch(setShowLogoutModal(true));
    } else if (templateName === TemplateTypes.WHATSAPP_CALL_INTERACTION) {
      navigateToCaseDetails(
        params?.customerReferenceId,
        params?.loanAccountNumber,
        params?.caseReferenceId,
        params?.businessVertical,
        navigate,
        APP_ROUTES.CASE_DETAIL.path,
        undefined,
        TAB_KEYS.COMMUNICATION_HISTORY
      );
    } else {
      navigateToCaseDetails(
        params?.customerReferenceId,
        params?.loanAccountNumber,
        params?.caseReferenceId,
        params?.businessVertical,
        navigate,
        APP_ROUTES.CASE_DETAIL.path
      );
    }
    markNotificationAsRead(notification, widget);
  };

  const markNotificationAsRead = (notification: NotificationsData, widget: Widget) => {
    const { params } = notification;
    toggleNotifications(false);
    const actionCompleted = !hasNotCompletedAction(notification);
    if (actionCompleted) {
      return;
    }
    const payload = {
      id: notification?.id,
      action: {
        widgetId: widget?.widgetId,
        widgetStatus: WidgetStatuses.CLICKED,
        timestamp: Date.now(),
        actionBy: phoneNumber
      }
    };
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Notifications_IndividualNotifClick, {
      loanAccountNumber: params?.loanAccountNumber,
      tab: activeTab
    });
    dispatch(triggerNotificationAction(payload));
  };

  const handleCtaClick = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    cta: ICTAS,
    notification: { notification: NotificationsData; widget: Widget }
  ) => {
    e.stopPropagation();
    const { actionType, canonicalIdentifier, data } = cta;
    let queryParams = '';
    switch (actionType) {
      case 'CALL':
        queryParams = `call*automatedSlashCall=${data?.phoneNumber}`;
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.Notifications_CallCTA, {
          ...data
        });
        break;
      case 'REDIRECT':
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.Notifications_ViewEMIsCTA, {
          ...data
        });
        break;
      default:
        break;
    }
    navigateToCaseDetails(
      data?.customerReferenceId,
      data?.loanAccountNumber,
      data?.caseReferenceId,
      data?.businessVertical,
      navigate,
      `${APP_ROUTES.CASE_DETAIL.path}`,
      queryParams,
      canonicalIdentifier
    );
    markNotificationAsRead(notification.notification, notification.widget);
  };

  const renderNotifList = useMemo(() => {
    const RenderedList = notifications.map((notification, idx) => {
      const { templateId } = notification.body;
      const template = templatesMap[templateId];
      const actionCompleted = !hasNotCompletedAction(notification);
      if (template.templateName === TemplateTypes.TELE_AGENTS_COSMOS_INACTIVITY_POPUP) return null;
      let isTemplateOfActiveTab = true;
      if (activeTab) {
        isTemplateOfActiveTab = NotificationTabTemplateMap[activeTab]?.includes(
          template.templateName
        );
      }
      if (!template || !isTemplateOfActiveTab) {
        return null;
      }
      if (!activeTab && idx > 4) return;
      return (
        <div
          key={notification.id}
          className={cx(styles['notificationList__item'], {
            [styles['notificationList__item__actionComplete']]: actionCompleted,
            [styles['notificationList__item__notif']]: !activeTab
          })}
          onClick={() =>
            handleNotificationClick(notification, template?.widgets[0], template?.templateName)
          }
        >
          <div className={styles['notificationList__item__icon']}>
            {TemplateInfoMap[template.templateName]?.icon}
          </div>
          <div>
            <div dangerouslySetInnerHTML={{ __html: notification.body.notificationText }} />
            {activeTab && (
              <div className={styles['notificationList__item__notification_bottom_container']}>
                <Typography variant="p5" className={styles['notificationList__item__time']}>
                  {getTimeDifference(notification.createdAt)}
                </Typography>
                <div className={styles['notificationList__item__catContainer']}>
                  {notification.ctas?.map((cta, idx) => {
                    return (
                      <span key={cta?.displayName}>
                        <span
                          key={idx}
                          className={styles['notificationList__item__cta']}
                          onClick={e => {
                            handleCtaClick(e, cta, { notification, widget: template.widgets[0] });
                          }}
                        >
                          {cta.displayName}
                        </span>
                        {notification?.ctas?.length && idx !== notification?.ctas?.length - 1 && (
                          <span className={styles['notificationList__item__cta__separator']}>
                            |
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {activeTab && (
            <div className={styles['notificationList__item__status']}>
              {!actionCompleted && (
                <div className={styles['notificationList__item__status__unseen']}></div>
              )}
            </div>
          )}
        </div>
      );
    });
    if (!activeTab && RenderedList.length > 5) {
      const hiddenNotifsCount = RenderedList.length - 5;
      RenderedList.push(
        <div className={styles.notifToastCount} onClick={() => toggleNotifications(true)}>
          +{hiddenNotifsCount} notification{hiddenNotifsCount > 1 ? 's' : ''}
        </div>
      );
    }
    return RenderedList;
  }, [activeTab, notifications]);

  return (
    <div className={cx(styles['notificationList'], { [styles.notifContainer]: !activeTab })}>
      {renderNotifList}
    </div>
  );
};

export default NotificationList;
